import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { IconLoader2 } from "@tabler/icons-react";
import { Field } from "@/types/Field";
import { Guest } from "@/types/Guest";
import { DataType } from "@/types/enums/DataType";
import { useFields } from "@/providers/FieldsProvider";
import { useRole } from "@/providers/RoleProvider";
import { canManageArrivalNotice, isArrivalNoticeGuest } from "./ArrivalNoticeLayoutCell";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { fetchOptionUsage } from "@/lib/api/handlers/AdditionalFieldHandling";
import { useConditionalFieldLock } from "../../PCreateGuest/components/useConditionalFieldLock";
import { CreationFieldsRef } from "../../PCreateGuest/PCreateGuest";
import { GuestSearch } from "../../Inputs/GuestSearch";
import { useAppointmentGuest } from "@/providers/AppointmentGuestProvider";
import { GuestLayoutFieldInput, LayoutFieldCtx } from "./GuestLayoutFieldInput";
import {
  CQ_FIELD_GRID,
  CQ_GROUP_GRID,
  CQ_GROUP_WIDTH_CLASS,
  CQ_WIDTH_CLASS,
  fieldByKey,
  isArrivalNoticeLayoutField,
  hasCustomLayout,
  resolveLayout,
} from "./guestDetailLayout";
import { useResolvedGuestDetailLayout } from "./useResolvedGuestDetailLayout";
import {
  EntityLaneSurface,
  isCreateSemantics,
  useCreateLayout,
  variantOfSurface,
} from "./createLayoutSurface";

/** Re-export — the constant lives in guestDetailLayout.ts so the resolution
 *  hook can import it without a component-module cycle. */
export { LAYOUT_FIELDS } from "./guestDetailLayout";

/** The three default group headings, already translated. Shared with the
 *  builder so both resolve the persisted layout identically. */
export function useDefaultGroupTitles() {
  const tPopup = useTranslations("popup-create-guest");
  return useMemo(
    () => ({
      general: tPopup("generalInfos"),
      contact: tPopup("contactInfos"),
      additional: tPopup("additionalFields"),
    }),
    [tPopup],
  );
}

type GuestDetailLayoutViewProps = {
  eventId?: string;
  creationFields: CreationFieldsRef;
  setCreationFieldsAction: (fields: CreationFieldsRef) => void;
  currentGuestId?: string;
  /** The fetched guest — value source for DISPLAY-ONLY cells. Undefined in the
   *  builder canvas and pre-create, where those cells render an em-dash. */
  guest?: Guest;
  onSaveCredential?: (fieldName: string, value: string) => Promise<void>;
  additionalFieldErrors?: Record<string, string>;
  onClearFieldError?: (fieldName: string) => void;
  /** IMAGE upload confirm persisted a value — host syncs form + baseline. */
  onAdditionalFieldPersisted?: (fieldName: string, value: unknown) => void;
  /** A cell's box disagrees with the form value — see LayoutFieldCtx. */
  onFieldInvalid?: (fieldName: string, invalid: boolean) => void;
  /**
   * Render one LANE's layout out of `detailLayouts.<surface>` instead of the
   * guest-detail popup's own `guestDetailLayout`. Absent = the detail popup,
   * byte-for-byte the previous behavior.
   *
   * Two families ride this prop and they differ in exactly one thing, the field
   * VOCABULARY (`variantOfSurface`): a `*Create` lane drops the credential and
   * display-only built-ins (see CREATE_PLACEABLE_BUILTIN_ORDER) because the
   * guest does not exist yet, while a `*Detail` request lane keeps the full
   * detail vocabulary — its guest exists and its `origin`/`numCompanions` are
   * decision input.
   */
  surface?: EntityLaneSurface;
  /** Create lanes only — the lane fixed the guest type (companion/registrant
   *  requests). See LayoutFieldCtx.guestTypeLocked. */
  guestTypeLocked?: boolean;
  /**
   * `surface` names where the LAYOUT comes from; this says the guest it is
   * rendered for ALREADY EXISTS — the pending-request popup, which decides on
   * its own lane's layout ("Anfrage — Gast" / "Anfrage — Begleitung") but is an
   * EDIT surface in every other respect.
   *
   * It matters for exactly one thing, and that one is load-bearing:
   * `ctx.isCreate` gates the additional-field DEFAULT fallback. On a guest that
   * exists, the stored value is authoritative and no lane ever injects a
   * default over it — painting one would show a value the database does not
   * hold and make the field uncleanable (see additionalFieldValue).
   */
  existingGuest?: boolean;
  /**
   * The popup shows the ARCHIVED list's guest — its header offers "Unarchive"
   * only, so a placed portal-link cell (W3-3) renders nothing either. Same
   * source as the header's `archived` (the list's archived flag).
   */
  archived?: boolean;
};

/**
 * Layout-driven replacement for the popup's hardcoded GeneralInfos /
 * ContactInfos / AdditionalFields stack: renders the event's (team-shared)
 * `guestDetailLayout` — named groups of fields on a 6-column grid with
 * per-field widths. Pure VIEW — editing happens in the dedicated
 * GuestLayoutBuilder popup (opened from the guest-detail header).
 */
export function GuestDetailLayoutView({
  eventId,
  creationFields,
  setCreationFieldsAction,
  currentGuestId,
  guest,
  onSaveCredential,
  additionalFieldErrors,
  onClearFieldError,
  onAdditionalFieldPersisted,
  onFieldInvalid,
  surface,
  guestTypeLocked,
  existingGuest = false,
  archived = false,
}: GuestDetailLayoutViewProps) {
  const t2 = useTranslations("guest-fields");
  // `plugins` for the portal-link cell — RoleProvider resolves them from the
  // EventProvider (the header action's source) or, on the builder pages, from
  // its own prop.
  const { legacyRoleFloor, plugins, role } = useRole();
  const { can } = useCapabilities();
  const canWriteGuests = can("guests:write");
  const viewOnly = legacyRoleFloor === "VIEWER";
  const { allFields } = useFields();

  // Selected layout — subscribed, so a builder save, another admin's save or a
  // picker change lands here live. BOTH hooks run on every render (hook order
  // is not negotiable) and each is handed a null eventId where it has no job,
  // with `useEvent` gating on `!!eventId`. A request lane is the one mode that
  // wants both: its own document, plus the guest-detail one to fall back to.
  //
  // Three axes, deliberately independent:
  //   `fromEntityLane` — WHERE the layout is stored (a lane document vs. the
  //                      guest-detail one),
  //   `variant`        — which FIELD vocabulary it resolves against,
  //   `isCreate`       — the VALUE semantics (defaults for a guest that does
  //                      not exist yet).
  // The request lanes are the combination that did not exist before: a lane
  // document, the detail vocabulary, edit value semantics.
  const fromEntityLane = !!surface;
  const variant = surface ? variantOfSurface(surface) : "detail";
  const isCreate = isCreateSemantics(surface, existingGuest);
  // A request lane FALLS BACK to the guest-detail layout until it is configured
  // — so an event that curated its guest popup keeps exactly that picture on
  // the decision surface, and nothing an admin hid there reappears. The detail
  // query therefore runs for the detail popup AND for the request lanes; only a
  // create dialog switches it off (there is nothing to fall back to on a lane
  // whose whole point is a blank form).
  const laneFallsBack = variant === "detail";
  const detailLayoutQ = useResolvedGuestDetailLayout(
    !fromEntityLane || laneFallsBack ? eventId : null,
  );
  const createLayoutQ = useCreateLayout(
    fromEntityLane ? eventId : null,
    surface ?? "guestCreate",
  );
  // `hasCustomLayout`, not `??`: "restore default" PERSISTS `{groups: [],
  // hidden: []}`, and a nullish check would read that as "configured" and show
  // the built-in default instead of falling back.
  const selectedLayout = !fromEntityLane
    ? detailLayoutQ.layout
    : hasCustomLayout(createLayoutQ.layout) || !laneFallsBack
      ? createLayoutQ.layout
      : detailLayoutQ.layout;
  const layoutPending =
    (fromEntityLane && createLayoutQ.isPending) ||
    ((!fromEntityLane || laneFallsBack) && detailLayoutQ.isPending);
  const defaultTitles = useDefaultGroupTitles();
  const layout = useMemo(
    () => resolveLayout(selectedLayout, allFields, defaultTitles, variant),
    [selectedLayout, allFields, defaultTitles, variant],
  );

  const { getLock, optionFiltered, confirmDialog } = useConditionalFieldLock(
    creationFields,
    allFields,
  );

  // ---- additional-field option capacity (ported from AdditionFields) -------
  const relevantAdditional = useMemo(
    () => allFields.filter((f) => f.additionalField),
    [allFields],
  );
  const [optionUsage, setOptionUsage] = useState<
    Record<string, Record<string, number>>
  >({});
  const cappedSelectFields = useMemo(
    () =>
      relevantAdditional.filter(
        (f) =>
          (f.dataType === DataType.SINGLE_SELECT ||
            f.dataType === DataType.MULTI_SELECT) &&
          f.fieldId &&
          f.optionCapacity &&
          Object.keys(f.optionCapacity).length > 0,
      ),
    [relevantAdditional],
  );
  useEffect(() => {
    if (!eventId || cappedSelectFields.length === 0) return;
    let alive = true;
    Promise.all(
      cappedSelectFields.map((f) =>
        fetchOptionUsage(eventId, f.fieldId!).then((u) => [f.fieldId!, u] as const),
      ),
    ).then((entries) => {
      if (!alive) return;
      const map: Record<string, Record<string, number>> = {};
      for (const [id, u] of entries) if (u) map[id] = u;
      setOptionUsage(map);
    });
    return () => {
      alive = false;
    };
  }, [eventId, cappedSelectFields]);

  const fullOptionsFor = useCallback(
    (field: Field): string[] | undefined => {
      const cap = field.optionCapacity;
      if (!cap || Object.keys(cap).length === 0 || !field.fieldId) return undefined;
      const usage = optionUsage[field.fieldId] ?? {};
      const raw: unknown =
        creationFields.additionalFields?.[field.name] ?? field.defaultValue;
      const held = new Set(
        Array.isArray(raw)
          ? raw.map(String)
          : raw != null && raw !== ""
            ? [String(raw)]
            : [],
      );
      const full: string[] = [];
      for (const [opt, c] of Object.entries(cap)) {
        if (held.has(opt)) continue;
        if ((usage[opt] ?? 0) >= c) full.push(opt);
      }
      return full.length > 0 ? full : undefined;
    },
    [optionUsage, creationFields],
  );

  // Scroll the first bound-violation into view after a failed save.
  useEffect(() => {
    if (!additionalFieldErrors || Object.keys(additionalFieldErrors).length === 0)
      return;
    const target = document.querySelector('[data-af-error="true"]');
    if (target && "scrollIntoView" in target) {
      (target as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [additionalFieldErrors]);

  // ---- companion main-guest label (ported from GeneralInfos) ---------------
  const { getRelevantGuestName } = useAppointmentGuest();
  const [mainGuestName, setMainGuestName] = useState("");
  useEffect(() => {
    let isMounted = true;
    const fetchGuestName = async () => {
      if (creationFields.guestType === "companion" && creationFields.mainGuest) {
        const dto = creationFields.mainGuestDTO;
        if (dto && dto.guestId === creationFields.mainGuest) {
          const name = [dto.firstName, dto.lastName].filter(Boolean).join(" ").trim();
          if (isMounted) setMainGuestName(name);
          return;
        }
        const name = await getRelevantGuestName(creationFields.mainGuest || "", true);
        if (isMounted) setMainGuestName(name);
      } else {
        if (isMounted) setMainGuestName("");
      }
    };
    fetchGuestName();
    return () => {
      isMounted = false;
    };
  }, [
    creationFields.guestType,
    creationFields.mainGuest,
    creationFields.mainGuestDTO,
    getRelevantGuestName,
  ]);

  const ctx: LayoutFieldCtx = {
    isCreate,
    creationFields,
    setCreationFields: setCreationFieldsAction,
    canWriteGuests,
    viewOnly,
    getLock,
    optionFiltered,
    onSaveCredential,
    fieldErrors: additionalFieldErrors,
    onClearFieldError,
    fullOptionsFor,
    currentGuestId,
    guest,
    onAdditionalFieldPersisted,
    onFieldInvalid,
    guestTypeLocked,
    eventId,
    plugins,
    // No portal link where the header offers none: an archived guest, and a
    // pending request's decision surface (a lane document resolved with the
    // detail vocabulary — the popup strips every header action there).
    portalLinkHidden: archived || (fromEntityLane && variant === "detail"),
    mainGuestName: mainGuestName || undefined,
  };

  // plan arrival-automation Rev. 4 §1.8 — see the cell loop below.
  const arrivalNoticeShown =
    !!guest &&
    !!eventId &&
    canManageArrivalNotice(role) &&
    isArrivalNoticeGuest(guest, archived) &&
    !(fromEntityLane && variant === "detail");

  const isCompanion = creationFields.guestType === "companion";
  const guestTypePlaced = layout.groups.some((g) =>
    g.fields.some((f) => f.key === "guestType"),
  );

  const mainGuestSearch = (
    <GuestSearch
      labelMargin="mb-1"
      disabled={!canWriteGuests}
      labelFontSize="body-3-medium"
      label={t2("mainGuest")}
      hoverMessage={t2("mainGuestOnlyHint")}
      mainGuestOnly={true}
      defaultValue={mainGuestName}
      excludeGuestId={currentGuestId}
      setSelectedGuest={(guest) => {
        setCreationFieldsAction({
          ...creationFields,
          mainGuest: (guest?.guestId as string) ?? "",
          mainGuestDTO: guest ?? undefined,
        });
      }}
    />
  );

  // Keep the first render stable while the layout query is in flight, so the
  // popup doesn't flash default -> custom.
  if (eventId && layoutPending) {
    return (
      <div className="flex items-center justify-center py-10">
        <IconLoader2 className="animate-spin font-tertiary" size={20} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 w-full @container">
      {/* A companion's link to its main guest must never be hideable collateral
          of removing the guestType field from the layout — without it the
          linkage would be invisible AND unchangeable. Fallback slot on top. */}
      {isCompanion && !guestTypePlaced && (
        <div className="w-full @md:w-1/2">{mainGuestSearch}</div>
      )}

      {/* Groups on a CONTAINER-query grid: HALF groups sit side by side (the
          classic Allgemein | Kontakt shape) when the panel is wide enough,
          FULL groups span the row. Each group is its own container so its
          fields degrade 6→2→1 columns with the group's real width instead of
          crushing. */}
      <div className={CQ_GROUP_GRID}>
        {layout.groups.map((group) => {
          // audit hop3 F9: a group that holds nothing but the hidden
          // "Ankunft melden an" cell is skipped whole — no title over an
          // empty grid. Narrow on purpose: a group empty for any other reason
          // renders as it always did.
          const hiddenArrival = group.fields.some((entry) => {
            const f = fieldByKey(allFields, entry.key, variant);
            return !!f && isArrivalNoticeLayoutField(f) && !arrivalNoticeShown;
          });
          if (
            hiddenArrival &&
            group.fields.every((entry) => {
              const f = fieldByKey(allFields, entry.key, variant);
              return !f || isArrivalNoticeLayoutField(f);
            })
          ) {
            return null;
          }
          return (
          <div
            key={group.id}
            className={`flex flex-col gap-3 min-w-0 @container ${
              CQ_GROUP_WIDTH_CLASS[group.width ?? "FULL"]
            }`}
          >
            {group.title && <p className="body-1-bold">{group.title}</p>}
            <div className={CQ_FIELD_GRID}>
              {group.fields.map((entry) => {
                const field = fieldByKey(allFields, entry.key, variant);
                if (!field) return null;
                // plan arrival-automation Rev. 4 §1.8: the "Ankunft melden an"
                // cell exists only for OWNER / ADMIN and a guest who can
                // arrive — skipped here, so no empty grid slot remains.
                if (isArrivalNoticeLayoutField(field) && !arrivalNoticeShown) return null;
                return (
                  <div key={entry.key} className={CQ_WIDTH_CLASS[entry.width ?? "HALF"]}>
                    <GuestLayoutFieldInput field={field} ctx={ctx} />
                    {/* Companion link rides directly under guestType, exactly
                        like the pre-layout popup. */}
                    {!field.additionalField &&
                      field.name === "guestType" &&
                      isCompanion && <div className="mt-3">{mainGuestSearch}</div>}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      {confirmDialog}
    </div>
  );
}
